import type { ChatItem, ToolUseItem } from '../../../../contracts/harness.types';
import { CREATE_TODO_PLAN_TOOL_NAME, TODO_TOOL_NAME } from '../agent-todo/types';
import { deriveWorkRows } from '../working-line/workRows';

import type {
  TimelinePolicy,
  TimelinePresentation,
  ToolUseGroupDecision,
  WorkingBlockDecision,
} from './types';

export type {
  TimelinePolicy,
  TimelinePresentation,
  ToolAxis,
  ToolClassification,
  ToolUseGroupDecision,
  WorkingBlockDecision,
} from './types';

const DEFAULT_MIN_GROUP_SIZE = 3;

export const isWorkingBlockWorkItem = (
  item: ChatItem,
  policy: TimelinePolicy
): boolean => {
  if (item.kind === 'thinking') return true;
  if (item.kind !== 'tool_use') return false;
  if (item.name === CREATE_TODO_PLAN_TOOL_NAME) return false;
  if (policy.isSuppressedTodo(item)) return true;
  // Activity (including tools with an opt-in card) stays inside the Working
  // block. Only interactions — pending approval, clarifications, plan/todo —
  // render standalone.
  return policy.classify(item).axis === 'activity';
};

/** Invisible failures are never rows, so a Working block's line never names one. */
export const isVisibleWorkItem = (item: ChatItem, policy: TimelinePolicy): boolean => {
  if (!isWorkingBlockWorkItem(item, policy)) return false;
  if (item.kind === 'thinking') return item.text.length > 0;
  if (item.kind !== 'tool_use') return false;
  if (item.name === TODO_TOOL_NAME) return false;
  if (item.status !== 'error') return true;
  return !policy.isSuppressedError(item);
};

/**
 * Live while the turn is in flight and either a member is still producing or
 * nothing has followed the section yet (`openEnded`).
 */
export const isSectionLive = (
  members: ChatItem[],
  turnInFlight: boolean,
  openEnded = false
): boolean =>
  turnInFlight &&
  (openEnded ||
    members.some(
      (m) =>
        (m.kind === 'thinking' && m.streaming) ||
        (m.kind === 'assistant_text' && m.streaming) ||
        (m.kind === 'tool_use' &&
          (m.status === 'running' ||
            m.status === 'queued' ||
            m.status === 'streaming_input'))
    ));

export const isGroupableToolUse = (
  item: ChatItem,
  policy: TimelinePolicy
): item is ToolUseItem => item.kind === 'tool_use' && policy.classify(item).groupable;

const isTransparentItem = (
  item: ChatItem,
  policy: TimelinePolicy,
  isRoutineRunSeed: boolean
): boolean =>
  (item.kind === 'user_message' &&
    !policy.isVisibleUserMessage(item, isRoutineRunSeed)) ||
  (item.kind === 'assistant_text' && !item.text);

const estimatedSpanFor = (
  items: ChatItem[],
  runStart: number,
  members: ChatItem[]
): { startedAt: string; endedAt: string } | undefined => {
  const previous = items[runStart - 1];
  const startedAt =
    previous?.kind === 'prompt_context_failed' ? undefined : previous?.createdAt;
  if (startedAt === undefined) return undefined;
  const startedMs = Date.parse(startedAt);
  if (!Number.isFinite(startedMs)) return undefined;

  let endedAt: string | undefined;
  let endedMs = -Infinity;
  for (const member of members) {
    if (member.kind === 'prompt_context_failed') continue;
    const stamp =
      member.kind === 'tool_use'
        ? (member.resultCreatedAt ?? member.createdAt)
        : member.createdAt;
    if (stamp === undefined) continue;
    const ms = Date.parse(stamp);
    if (Number.isFinite(ms) && ms > endedMs) {
      endedMs = ms;
      endedAt = stamp;
    }
  }

  if (endedAt === undefined || endedMs < startedMs) return undefined;
  return { startedAt, endedAt };
};

const sectionSpan = (
  items: ChatItem[],
  runStart: number,
  members: ChatItem[],
  closer: ChatItem | undefined
): {
  liveSpanMs?: { first: number; last: number };
  estimatedSpan?: { startedAt: string; endedAt: string };
} => {
  let liveSpanMs: { first: number; last: number } | undefined;
  for (const member of members) {
    const seen = member.kind === 'prompt_context_failed' ? undefined : member.seenAtMs;
    if (!seen) continue;
    liveSpanMs = liveSpanMs
      ? {
          first: Math.min(liveSpanMs.first, seen.first),
          last: Math.max(liveSpanMs.last, seen.last),
        }
      : seen;
  }

  const closerSeen =
    closer === undefined || closer.kind === 'prompt_context_failed'
      ? undefined
      : closer.seenAtMs;
  if (liveSpanMs !== undefined && closerSeen !== undefined) {
    liveSpanMs = {
      first: liveSpanMs.first,
      last: Math.max(liveSpanMs.last, closerSeen.first),
    };
  }

  const estimatedSpan = estimatedSpanFor(items, runStart, members);
  return {
    ...(liveSpanMs !== undefined ? { liveSpanMs } : {}),
    ...(estimatedSpan !== undefined ? { estimatedSpan } : {}),
  };
};

const sectionIdFor = (head: ChatItem): string | undefined => {
  if (head.kind !== 'thinking' && head.kind !== 'tool_use') return undefined;
  if (head.turnId === undefined && head.iteration === undefined) return undefined;
  return `${head.turnId ?? ''}:${head.iteration ?? ''}:${head.id}`;
};

const closedByOf = (closer: ChatItem | undefined): 'answer' | 'other' | null => {
  if (closer === undefined) return null;
  return closer.kind === 'assistant_text' ? 'answer' : 'other';
};

interface WorkingRun {
  members: ChatItem[];
  /** The item that ended the run, if any. */
  closer: ChatItem | undefined;
  runStart: number;
}

/**
 * Each maximal run of work items (with the transparent items between them) that
 * holds visible work: the sections, before any presentation is derived for them.
 */
const workingRuns = (items: ChatItem[], policy: TimelinePolicy): WorkingRun[] => {
  const runs: WorkingRun[] = [];
  const firstUserMessageId = items.find((entry) => entry.kind === 'user_message')?.id;
  const isMember = (item: ChatItem): boolean =>
    isWorkingBlockWorkItem(item, policy) ||
    isTransparentItem(item, policy, item.id === firstUserMessageId);

  let runStart = 0;
  while (runStart < items.length) {
    const head = items[runStart];
    if (!head || !isWorkingBlockWorkItem(head, policy)) {
      runStart += 1;
      continue;
    }

    let runEnd = runStart + 1;
    const members: ChatItem[] = [head];
    while (runEnd < items.length) {
      const next = items[runEnd];
      if (!next || !isMember(next)) break;
      members.push(next);
      runEnd += 1;
    }

    if (members.some((member) => isVisibleWorkItem(member, policy))) {
      runs.push({ members, closer: items[runEnd], runStart });
    }
    runStart = runEnd;
  }
  return runs;
};

const computeWorkingBlocks = (
  items: ChatItem[],
  policy: TimelinePolicy,
  groups: ReadonlyMap<string, ToolUseGroupDecision> = computeToolUseGroups(items, policy)
): Map<string, WorkingBlockDecision> => {
  const decisions = new Map<string, WorkingBlockDecision>();
  for (const { members, closer, runStart } of workingRuns(items, policy)) {
    const [head] = members;
    if (!head) continue;
    const sectionId = sectionIdFor(head);
    decisions.set(head.id, {
      role: 'start',
      members,
      openEnded: closer === undefined,
      rows: deriveWorkRows(members, (item) => isVisibleWorkItem(item, policy), groups),
      closedBy: closedByOf(closer),
      ...(sectionId !== undefined ? { sectionId } : {}),
      ...sectionSpan(items, runStart, members, closer),
    });
    for (const member of members.slice(1)) {
      decisions.set(member.id, { role: 'member' });
    }
  }
  return decisions;
};

/**
 * A live Working block is the turn's one progress indicator: its line names the
 * work, or rotates the turn's loader phrases while there is nothing to name. The
 * phrase shimmer below the timeline therefore stays down for as long as any
 * section is live, queued calls and the gaps between calls included.
 */
export const hasLiveWorkingSection = (
  items: ChatItem[],
  policy: TimelinePolicy,
  turnInFlight: boolean
): boolean => {
  if (!turnInFlight) return false;
  // Runs only: this runs every render, and needs no groups or rows.
  return workingRuns(items, policy).some(({ members, closer }) =>
    isSectionLive(members, turnInFlight, closer === undefined)
  );
};

const computeToolUseGroups = (
  items: ChatItem[],
  policy: TimelinePolicy
): Map<string, ToolUseGroupDecision> => {
  const decisions = new Map<string, ToolUseGroupDecision>();
  const minGroupSize = policy.minGroupSize ?? DEFAULT_MIN_GROUP_SIZE;

  let runStart = 0;
  while (runStart < items.length) {
    const head = items[runStart];
    if (!head || !isGroupableToolUse(head, policy)) {
      runStart += 1;
      continue;
    }

    const headClassification = policy.classify(head);
    const groupKey = headClassification.groupKey ?? head.name;
    const groupMinSize = headClassification.minGroupSize ?? minGroupSize;
    let runEnd = runStart + 1;
    const members: ToolUseItem[] = [head];
    let runIteration = head.iteration;
    while (runEnd < items.length) {
      const next = items[runEnd];
      if (!next) break;
      // The plan card is the sole representation of absorbed todo updates, so
      // it must not prevent adjacent artifact creates from forming one batch.
      if (next.kind === 'tool_use' && policy.isSuppressedTodo(next)) {
        runEnd += 1;
        continue;
      }
      if (!isGroupableToolUse(next, policy)) break;
      const nextClassification = policy.classify(next);
      if ((nextClassification.groupKey ?? next.name) !== groupKey) break;
      if (
        runIteration !== undefined &&
        next.iteration !== undefined &&
        next.iteration !== runIteration
      ) {
        break;
      }
      runIteration ??= next.iteration;
      members.push(next);
      runEnd += 1;
    }

    if (members.length >= groupMinSize) {
      decisions.set(head.id, { role: 'start', members });
      for (const member of members.slice(1)) {
        decisions.set(member.id, { role: 'member' });
      }
    }

    runStart = runEnd;
  }

  return decisions;
};

export const deriveTimelinePresentation = (
  items: ChatItem[],
  policy: TimelinePolicy
): TimelinePresentation => {
  const groups = computeToolUseGroups(items, policy);
  return { sections: computeWorkingBlocks(items, policy, groups), groups };
};
